import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { zodResolver } from '@hookform/resolvers/zod';

import {
  Alert,
  Box,
  Button,
  Field,
  Flex,
  PasswordInput,
  Text,
} from '@valwin/facto/components';

import { useErrorTranslator } from '~shared/i18n';
import { usePharmacyId } from '~shared/routes';

import {
  type ResetTokenFormData,
  ResetTokenFormErrors,
  resetTokenSchema,
} from '../model/reset-token.schema';
import { usePostResetToken } from '../model/usePostResetToken';

type ResetTokenFormProps = {
  isOTP: boolean;
};

export function ResetTokenForm({ isOTP }: ResetTokenFormProps) {
  const { t } = useTranslation();
  const pharmacyId = usePharmacyId();
  const { token } = useParams();

  const { submitNewPassword, reset, isPending, isSuccess, isError } =
    usePostResetToken({ pharmacyId, token, isOTP });

  const translateError = useErrorTranslator<ResetTokenFormErrors>({
    PASSWORD_REQUIRED: () => t('auth newPasswordRequired'),
    PASSWORDS_MISMATCH: () => t('errors invalid'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetTokenFormData>({
    resolver: zodResolver(resetTokenSchema),
    defaultValues: { password: '', password2: '' },
  });

  const onSubmit = handleSubmit((data) => {
    submitNewPassword(data);
  });

  const handleInputChange = () => {
    if (isError) {
      reset();
    }
  };

  return (
    <Flex flexDirection="column" gap="8" width="full" maxWidth="sm">
      <Text textStyle="sm" color="gray.500" textAlign="center" marginBottom={0}>
        {t('auth resetPassword')}
      </Text>
      <form onSubmit={onSubmit} noValidate>
        <Box display="flex" flexDirection="column" gap={4}>
          <Field.Root required invalid={!!errors.password?.message}>
            <Field.Label>
              {t('auth newPassword')} <Field.RequiredIndicator />
            </Field.Label>
            <PasswordInput
              placeholder={t('auth newPasswordPlaceholder')}
              {...register('password', {
                onChange: handleInputChange,
              })}
            />
            <Field.ErrorText>
              {errors.password?.message &&
                translateError(errors.password.message)}
            </Field.ErrorText>
          </Field.Root>

          <Field.Root required invalid={!!errors.password2?.message}>
            <Field.Label>
              {t('auth resetToken confirm')} <Field.RequiredIndicator />
            </Field.Label>
            <PasswordInput
              placeholder={t('auth newPasswordPlaceholder')}
              {...register('password2', {
                onChange: handleInputChange,
              })}
            />
            <Field.ErrorText>
              {errors.password2?.message &&
                translateError(errors.password2.message)}
            </Field.ErrorText>
          </Field.Root>

          {isSuccess && (
            <Alert.Root status="success">
              <Alert.Indicator />
              <Alert.Title>{t('auth passwordResetSuccess')}</Alert.Title>
            </Alert.Root>
          )}

          {isError && (
            <Alert.Root status="error">
              <Alert.Indicator />
              <Alert.Title>{t('auth resetPasswordError')}</Alert.Title>
            </Alert.Root>
          )}

          <Flex alignItems="center" marginTop={4}>
            <Button
              type="submit"
              size="sm"
              loading={isPending}
              disabled={isSubmitting || isPending || isSuccess}
              flex={1}
            >
              {t('auth validate')}
            </Button>
          </Flex>
        </Box>
      </form>
    </Flex>
  );
}
